import { createSelector, Selector } from '@reduxjs/toolkit';
import { InferSocketResponse, InferSocketSearchResponse, SentinelKey } from 'src/utils/socket';
import { AppState } from '../types';
import { SentinelCacheObject, SentinelState } from './types';
import { isCacheObjectExpired } from './utils';

const selectSentinel = (state: AppState) => state.sentinel;

const sentinelCacheByKeySelectors: { [K in SentinelKey]: Selector<AppState, SentinelState[K]> } = {
  controllers: createSelector(selectSentinel, state => state.controllers),
  firmware: createSelector(selectSentinel, state => state.firmware),
  organizations: createSelector(selectSentinel, state => state.organizations),
  logs: createSelector(selectSentinel, state => state.logs),
  annotations: createSelector(selectSentinel, state => state.annotations),
  users: createSelector(selectSentinel, state => state.users),
  bundles: createSelector(selectSentinel, state => state.bundles),
};

const sentinelDataByKeySelectors: { [K in SentinelKey]: Selector<AppState, SentinelState[K]['data']> } = {
  controllers: createSelector(sentinelCacheByKeySelectors.controllers, state => state.data),
  firmware: createSelector(sentinelCacheByKeySelectors.firmware, state => state.data),
  organizations: createSelector(sentinelCacheByKeySelectors.organizations, state => state.data),
  logs: createSelector(sentinelCacheByKeySelectors.logs, state => state.data),
  annotations: createSelector(sentinelCacheByKeySelectors.annotations, state => state.data),
  users: createSelector(sentinelCacheByKeySelectors.users, state => state.data),
  bundles: createSelector(sentinelCacheByKeySelectors.bundles, state => state.data),
};

const sentinelQueryByKeySelectors: { [K in SentinelKey]: Selector<AppState, SentinelState[K]['query']> } = {
  controllers: createSelector(sentinelCacheByKeySelectors.controllers, state => state.query),
  firmware: createSelector(sentinelCacheByKeySelectors.firmware, state => state.query),
  organizations: createSelector(sentinelCacheByKeySelectors.organizations, state => state.query),
  logs: createSelector(sentinelCacheByKeySelectors.logs, state => state.query),
  annotations: createSelector(sentinelCacheByKeySelectors.annotations, state => state.query),
  users: createSelector(sentinelCacheByKeySelectors.users, state => state.query),
  bundles: createSelector(sentinelCacheByKeySelectors.bundles, state => state.query),
};

/** Selects a single item from the cache by key and id. */
export const createSentinelDataByIdSelector = <K extends SentinelKey>(
  key: K,
  id: string | undefined,
): Selector<AppState, SentinelCacheObject<InferSocketResponse<K>> | undefined> => {
  // The Selector<S,R> type seems to widen R to all possible values of K, even when we have a specific K. This cast is the actual correct type.
  return createSelector(sentinelDataByKeySelectors[key], state => {
    if (id === undefined) return undefined;
    const item = state[id];
    if (!item || isCacheObjectExpired(item)) return undefined;
    return item;
  }) as unknown as Selector<AppState, SentinelCacheObject<InferSocketResponse<K>> | undefined>;
};

/**
 * Selects a list of items from the cache by key and mongo query object.
 * Note that this does not actually evaluate the query! It only looks up whether the
 * query has been evaluated before, and if so, returns the cached resuls.
 */
export const createSentinelDataByQuerySelector = <K extends SentinelKey>(
  key: K,
  query: any,
): Selector<AppState, Array<InferSocketResponse<K>>> => {
  return createSelector(sentinelQueryByKeySelectors[key], sentinelDataByKeySelectors[key], (queryLookup, data) => {
    if (!query) return [];
    const queryKey = JSON.stringify(query);
    const lookupItems = queryLookup[queryKey]?.items;
    if (!lookupItems) return [];
    return lookupItems.map(i => data[i]).filter(i => !!i);
  }) as unknown as Selector<AppState, Array<InferSocketResponse<K>>>;
};

export const createSentinelSearchDataByQuerySelector = <K extends SentinelKey>(
  key: K,
  query: any,
): Selector<AppState, InferSocketSearchResponse<K>> => {
  return createSelector(sentinelQueryByKeySelectors[key], sentinelDataByKeySelectors[key], (queryLookup, data) => {
    const emptyResult: InferSocketSearchResponse<K> = {
      results: [],
      totalCount: 0,
      relations: undefined,
    };
    if (!query) return emptyResult;
    const queryKey = JSON.stringify(query);
    const lookup: any = queryLookup[queryKey];
    if (!lookup) return emptyResult;
    const items = (lookup.items as string[])?.map(i => data[i])?.filter(i => !!i);
    return {
      results: items,
      totalCount: lookup?.responseMeta?.totalCount,
      relations: lookup?.responseMeta?.relations,
    };
  }) as unknown as Selector<AppState, InferSocketSearchResponse<K>>;
};
